/**
 * IA:s baspaket fördelat på estimatets rader — det kunden ser per rad.
 *
 * Baspaketet (kategorin `inredningsarkitekt`) visas aldrig som egen rad. Det
 * läggs på raderna i proportion till deras storlek, så radpriserna summerar
 * till totalen.
 *
 * PROBLEMET med ren proportion: lägger BK till en rad i nästa version krymper
 * alla andra raders andel. Badrummet för 300 000 kr visades som 311 400 kr i
 * version 1.0 och 308 400 kr i 1.1 utan att något hänt med badrummet — och
 * kunden kunde jämföra versionerna sida vid sida.
 *
 * LÖSNINGEN är låsta andelar. När en version skickas sparas varje rads andel i
 * kronor (`Estimat.baspaketAndelar`), och nästa version ärver dem:
 *
 *   - En rad med låst andel behåller den. Ändras radens grundpris ändras
 *     radpriset med exakt den ändringen.
 *   - Det som blir över (rader har tagits bort, baspaketet har vuxit) fördelas
 *     på de NYA raderna. Finns inga nya rader fördelas det på alla — det enda
 *     läget där en befintlig rad rör sig.
 *   - Krymper baspaketet fördelas minskningen på alla rader.
 *
 * Saknas låsta andelar (estimat skickade före 2026-11, och första versionen)
 * räknas som förut, se `radpriserProportionellt`.
 *
 * Används av frontendens kundvy och summering (features/estimat/domain/
 * calculations.js) och av backenden när en version skickas.
 */

const RADKATEGORIER = ['projektering', 'byggoffert', 'sidoentreprenorer', 'inkopFastInredning'];
const ETABLERING = 'byggoffert_etablering';

const round100 = (v) => Math.round(v / 100) * 100;

/** Samma som calculateItemTotal i frontend: radens pris inklusive underrader. */
const radTotal = (item) => {
  if (!item || !item.selected) return 0;

  const subItemsTotal = (item.subItems || []).reduce((sum, sub) => {
    if (sub && sub.selected && sub.quantity > 0) {
      const subPrice = typeof sub.price === 'object' ? (sub.price?.totalt || 0) : (sub.price || 0);
      return sum + (subPrice * sub.quantity);
    }
    return sum;
  }, 0);

  if (item.useSubItemsTotal) return subItemsTotal;

  const itemPrice = typeof item.price === 'object' ? (item.price?.totalt || 0) : (item.price || 0);
  return itemPrice * (item.quantity || 0) + subItemsTotal;
};

/** Grunddata: kategorisummor, etableringens bas och baspaketets storlek. */
const grunddata = (kategorier, projectInfo) => {
  const summor = { projektering: 0, byggoffert: 0, byggoffertEtablering: 0, sidoentreprenorer: 0, inkopFastInredning: 0 };

  ['projektering', 'sidoentreprenorer', 'inkopFastInredning'].forEach((key) => {
    Object.values(kategorier[key] || {}).forEach((it) => { summor[key] += radTotal(it); });
  });

  let etableringBase = 0;
  Object.entries(kategorier.byggoffert || {}).forEach(([k, it]) => {
    const t = radTotal(it);
    if (t > 0) {
      summor.byggoffert += t;
      if (!k.includes('Altan') && !k.includes('Ställning')) etableringBase += t;
    }
  });

  const etableringPercent = Math.max(0, Number(projectInfo?.etableringPercent ?? 10)) / 100;
  summor.byggoffertEtablering = etableringBase * etableringPercent;

  let baspaket = 0;
  Object.values(kategorier.inredningsarkitekt || {}).forEach((it) => { baspaket += radTotal(it); });

  const totalUtanBaspaket = summor.projektering + summor.byggoffert + summor.byggoffertEtablering
    + summor.sidoentreprenorer + summor.inkopFastInredning;

  return { summor, baspaket, totalUtanBaspaket };
};

/**
 * Den ursprungliga, rent proportionella fördelningen. Oförändrad — estimat
 * utan låsta andelar ska se ut exakt som de alltid gjort.
 */
const radpriserProportionellt = (kategorier, projectInfo = {}) => {
  const result = {};
  if (!kategorier) return result;

  const { summor, baspaket, totalUtanBaspaket } = grunddata(kategorier, projectInfo);

  if (baspaket === 0 || totalUtanBaspaket === 0) {
    RADKATEGORIER.forEach((categoryKey) => {
      Object.entries(kategorier[categoryKey] || {}).forEach(([itemName, item]) => {
        const t = radTotal(item);
        if (t > 0) result[`${categoryKey}_${itemName}`] = t;
      });
    });
    result[ETABLERING] = summor.byggoffertEtablering;
    return result;
  }

  const perKategori = {
    projektering: Math.round(baspaket * (summor.projektering / totalUtanBaspaket)),
    byggoffert: Math.round(baspaket * (summor.byggoffert / totalUtanBaspaket)),
    byggoffertEtablering: Math.round(baspaket * (summor.byggoffertEtablering / totalUtanBaspaket)),
    sidoentreprenorer: Math.round(baspaket * (summor.sidoentreprenorer / totalUtanBaspaket)),
    inkopFastInredning: Math.round(baspaket * (summor.inkopFastInredning / totalUtanBaspaket))
  };
  const fordelat = Object.values(perKategori).reduce((s, v) => s + v, 0);
  perKategori.byggoffert += (baspaket - fordelat);

  // Sista raden i kategorin tar avrundningen, så raderna summerar till den
  // MAV-rundade kategorisumman.
  const fordelaKategori = (entries, prefix, kategoriTotal, kategoriBaspaket) => {
    if (kategoriTotal === 0) return;
    const rader = Object.entries(entries).filter(([, item]) => radTotal(item) > 0);
    if (rader.length === 0) return;

    const mal = round100(kategoriTotal + kategoriBaspaket);
    let summa = 0;
    rader.forEach(([itemName, item], index) => {
      const t = radTotal(item);
      const pris = index === rader.length - 1
        ? mal - summa
        : round100(t + Math.round(kategoriBaspaket * (t / kategoriTotal)));
      summa += pris;
      result[`${prefix}_${itemName}`] = pris;
    });
  };

  RADKATEGORIER.forEach((categoryKey) => {
    fordelaKategori(kategorier[categoryKey] || {}, categoryKey, summor[categoryKey], perKategori[categoryKey]);
  });
  result[ETABLERING] = round100(summor.byggoffertEtablering + perKategori.byggoffertEtablering);

  return result;
};

/** `baspaketAndelar` lagras som lista ({ nyckel, andel }) — radnamn kan innehålla punkter. */
const andelarSomObjekt = (lista) => {
  if (!Array.isArray(lista)) return null;
  const obj = {};
  lista.forEach((a) => {
    if (a && typeof a.nyckel === 'string' && Number.isFinite(Number(a.andel))) obj[a.nyckel] = Number(a.andel);
  });
  return Object.keys(obj).length > 0 ? obj : null;
};

/**
 * Radpriserna kunden ser: `{ "byggoffert_invändigt_Badrum - stort": 311400, ... }`.
 *
 * @param {Object} kategorier - estimatKategorier
 * @param {Object} projectInfo
 * @param {Array} laastaAndelar - Estimat.baspaketAndelar; tom/saknad = proportionellt
 */
const radpriserMedBaspaket = (kategorier, projectInfo = {}, laastaAndelar = null) => {
  const laasta = andelarSomObjekt(laastaAndelar);
  if (!kategorier || !laasta) return radpriserProportionellt(kategorier, projectInfo);

  const { summor, baspaket, totalUtanBaspaket } = grunddata(kategorier, projectInfo);
  if (baspaket === 0 || totalUtanBaspaket === 0) return radpriserProportionellt(kategorier, projectInfo);

  const rader = [];
  RADKATEGORIER.forEach((categoryKey) => {
    Object.entries(kategorier[categoryKey] || {}).forEach(([itemName, item]) => {
      const bas = radTotal(item);
      if (bas > 0) rader.push({ nyckel: `${categoryKey}_${itemName}`, bas });
    });
  });
  if (summor.byggoffertEtablering > 0) rader.push({ nyckel: ETABLERING, bas: summor.byggoffertEtablering });

  const andel = {};
  let laastSumma = 0;
  rader.forEach((r) => {
    if (laasta[r.nyckel] !== undefined) {
      andel[r.nyckel] = laasta[r.nyckel];
      laastSumma += laasta[r.nyckel];
    } else {
      andel[r.nyckel] = 0;
    }
  });

  const kvar = baspaket - laastSumma;
  const priser = {};
  rader.forEach((r) => { priser[r.nyckel] = r.bas + andel[r.nyckel]; });

  if (Math.abs(kvar) >= 1) {
    const nya = rader.filter((r) => laasta[r.nyckel] === undefined);
    const mottagare = kvar > 0 && nya.length > 0 ? nya : rader;
    const basSumma = mottagare.reduce((s, r) => s + r.bas, 0);

    // Mottagarna rundas till hela hundratal; den sista tar avrundningen så att
    // radernas summa blir exakt grundpriserna + baspaketet.
    const mal = rader.reduce((s, r) => s + r.bas, 0) + baspaket;
    let ovrigaSumma = rader
      .filter((r) => !mottagare.includes(r))
      .reduce((s, r) => s + priser[r.nyckel], 0);

    mottagare.forEach((r, index) => {
      if (index === mottagare.length - 1) {
        priser[r.nyckel] = mal - ovrigaSumma;
      } else {
        priser[r.nyckel] = round100(r.bas + andel[r.nyckel] + kvar * (r.bas / basSumma));
        ovrigaSumma += priser[r.nyckel];
      }
    });
  }

  return priser;
};

/**
 * Andelarna som ska låsas när en version skickas: radpris minus grundpris.
 * Räknas från det kunden faktiskt ser, så nästa version visar samma belopp.
 *
 * @returns {Array<{ nyckel: string, andel: number }>} tom om det inte finns något baspaket
 */
const beraknaLaastaAndelar = (kategorier, projectInfo = {}, laastaAndelar = null) => {
  if (!kategorier) return [];
  const { summor, baspaket, totalUtanBaspaket } = grunddata(kategorier, projectInfo);
  if (baspaket === 0 || totalUtanBaspaket === 0) return [];

  const priser = radpriserMedBaspaket(kategorier, projectInfo, laastaAndelar);
  const lista = [];
  RADKATEGORIER.forEach((categoryKey) => {
    Object.entries(kategorier[categoryKey] || {}).forEach(([itemName, item]) => {
      const nyckel = `${categoryKey}_${itemName}`;
      const bas = radTotal(item);
      if (bas > 0 && priser[nyckel] !== undefined) lista.push({ nyckel, andel: priser[nyckel] - bas });
    });
  });
  if (summor.byggoffertEtablering > 0 && priser[ETABLERING] !== undefined) {
    lista.push({ nyckel: ETABLERING, andel: priser[ETABLERING] - summor.byggoffertEtablering });
  }
  return lista;
};

/**
 * Kategorisummor inklusive baspaket, ur radpriserna — så att översikten och
 * raderna alltid visar samma siffror när andelarna är låsta.
 */
const kategorisummorUrRadpriser = (kategorier, priser) => {
  const summor = { projektering: 0, byggoffert: 0, sidoentreprenorer: 0, inkopFastInredning: 0 };
  RADKATEGORIER.forEach((categoryKey) => {
    Object.keys(kategorier?.[categoryKey] || {}).forEach((itemName) => {
      summor[categoryKey] += priser[`${categoryKey}_${itemName}`] || 0;
    });
  });
  summor.etablering = priser[ETABLERING] || 0;
  return summor;
};

module.exports = {
  radTotal,
  radpriserMedBaspaket,
  radpriserProportionellt,
  beraknaLaastaAndelar,
  kategorisummorUrRadpriser,
  andelarSomObjekt
};
